import React from 'react';
import SkeletonLoader from './SkeletonLoader';
import CheckoutFormSkeleton from './CheckoutFormSkeleton';
import OrderSummarySkeleton from './OrderSummarySkeleton';

/**
 * CheckoutSkeleton - Full page skeleton for checkout loading states
 *
 * Professional wrapper component following DRY principle and separation of concerns.
 * Encapsulates entire checkout skeleton layout (header + form + sidebar).
 *
 * Used during:
 * - Initial cart verification (loading state)
 * - Cart data fetching (isLoadingCart state)
 *
 * Implements Shopify/Zenvixy skeleton patterns:
 * - Minimal, clean design with thin bars (12px height)
 * - Professional shimmer effect (0.15 opacity, 2s animation)
 * - Proper grid layout (1fr | 570px | 480px | 1fr)
 * - Semi-transparent logo on dark header
 *
 * @param {Object} props
 * @param {boolean} props.showHeader - Whether to show header with logo skeleton (default: true)
 * @param {string} props.headerBackgroundColor - Header background color (default: '#2c3e50')
 * @param {boolean} props.showMobileHeader - Whether to show mobile summary header in sidebar (default: false)
 *
 * @example
 * // Initial page load with header
 * if (loading) {
 *   return <CheckoutSkeleton showHeader={true} />;
 * }
 *
 * @example
 * // Cart loading state without header (header already rendered)
 * {isLoadingCart ? (
 *   <CheckoutSkeleton showHeader={false} />
 * ) : (
 *   <ActualContent />
 * )}
 */
const CheckoutSkeleton = ({
  showHeader = true,
  headerBackgroundColor = '#2c3e50',
  showMobileHeader = false
}) => {
  return (
    <div className="checkout-container">
      {/* Header with logo skeleton */}
      {showHeader && (
        <div className="checkout-header" style={{ backgroundColor: headerBackgroundColor }}>
          <div className="logo">
            <SkeletonLoader
              width="150px"
              height="40px"
              borderRadius="4px"
              style={{ background: 'rgba(255, 255, 255, 0.1)' }}
            />
          </div>
        </div>
      )}

      {/* Professional Skeleton Loading - Shopify/Zenvixy Style */}
      <div className="checkout-content">
        {/* Left Column - Form Skeleton */}
        <div className="checkout-main">
          <CheckoutFormSkeleton />
        </div>

        {/* Right Column - Order Summary Skeleton */}
        <div className="checkout-sidebar">
          <OrderSummarySkeleton showMobileHeader={showMobileHeader} />
        </div>
      </div>
    </div>
  );
};

export default CheckoutSkeleton;
